import { CircleNotchIcon } from '@phosphor-icons/react'
import styles from './ScoopAvatar.module.scss'

/**
 * Get user initials from first and last name
 * @param {string} firstName - User's first name
 * @param {string} lastName - User's last name
 * @returns {string|null} User initials (e.g., "JM" for Juan Marengo, "JU" for Juan), or null if no names provided
 */
const getInitials = (firstName, lastName) => {
    if (!firstName && !lastName) return null

    // If we have both first and last name
    if (firstName && lastName) {
        return (firstName.charAt(0) + lastName.charAt(0)).toUpperCase()
    }

    // If we only have firstName, check if it contains a space (full name)
    if (firstName) {
        const names = firstName.trim().split(' ')
        if (names.length >= 2) {
            // Has multiple words: take first letter of first two words
            return (names[0].charAt(0) + names[1].charAt(0)).toUpperCase()
        }
        // Single word: take first two letters
        return firstName.substring(0, 2).toUpperCase()
    }

    // If we only have lastName (unlikely but handle it)
    if (lastName) {
        return lastName.substring(0, 2).toUpperCase()
    }

    return null
}

/**
 * UserAvatar - Display user initials in a circular avatar
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} [props.firstName] - User's first name
 * @param {string} [props.lastName] - User's last name
 * @param {'xs' | 'sm' | 'md' | 'lg'} [props.size='md'] - Avatar size
 *   - xs: 24px (navbar secondary)
 *   - sm: 32px (compact views)
 *   - md: 40px (navbar, default)
 *   - lg: 56px (profile pages)
 * @param {string} [props.className] - Additional CSS classes
 * @param {Object} [props.style] - Inline styles for override
 * @param {function} [props.onClick] - Click handler
 * @param {...any} props - All other props are passed to the div element
 */
export const ScoopAvatar = ({
    firstName,
    lastName,
    size = 'md',
    className = '',
    style,
    onClick,
    ...props
}) => {
    const initials = getInitials(firstName, lastName)

    const avatarClassName = [styles.avatar, styles[size], onClick && styles.clickable, className]
        .filter(Boolean)
        .join(' ')

    // Map avatar size to spinner size
    const spinnerSizeMap = {
        xs: 8,
        sm: 10,
        md: 14,
        lg: 20,
    }

    return (
        <div className={avatarClassName} style={style} onClick={onClick} {...props}>
            {initials ? (
                <span className={styles.initials}>{initials}</span>
            ) : (
                <CircleNotchIcon
                    size={spinnerSizeMap[size]}
                    weight="bold"
                    className={styles.spinner}
                />
            )}
        </div>
    )
}

export default ScoopAvatar
